/**
 * Capture a player snapshot and upload it to Threekit Files API.
 * @see https://developer.threekit.com/reference/snapshot
 * @see https://developer.threekit.com/reference/uploadfiles
 */

import { threekitTokenPreview } from "./token-preview";

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export type ThreekitSnapshotAsyncOptions = {
  size?: { width: number; height: number };
  mimeType?: "image/jpeg" | "image/png" | "image/webp";
  cameraId?: string;
};

export type SnapshotAndUploadOptions = {
  /** Passed to {@link https://developer.threekit.com/reference/snapshot player.snapshotAsync} */
  snapshot?: ThreekitSnapshotAsyncOptions;
  /** Multipart filename (default derived from mime) */
  filename?: string;
  /**
   * Threekit env subdomain to upload to (`preview` / `admin-fts`). Defaults to the `env` the
   * player was mounted with. Sent to the upload proxy as `environment`.
   */
  env?: string;
  /**
   * Org id that, together with `env`, picks the environment (dev / staging / prod) whose private
   * API token the upload proxy uses — required to tell staging and prod apart on `admin-fts`.
   * Defaults to the `orgId` the player was mounted with.
   */
  orgId?: string;
  /**
   * Log upload target and mode to the console (no tokens). Debug is also on when
   * `import.meta.env.DEV`, `VITE_THREEKIT_FILES_UPLOAD_DEBUG=true` (build-time), URL
   * `?threekitFilesUploadDebug=1`, or `localStorage.threekitFilesUploadDebug === "1"`.
   */
  debug?: boolean;
};

export type SnapshotAndUploadResult = {
  /**
   * Public https URL for the uploaded file (direct field from API when present, else
   * `{threekitFilesApiUrl(env)}/{id}/content`).
   */
  url: string;
  /** First entry from the Files API `files` array */
  file: unknown;
  /** Full JSON body from the Files API */
  raw: unknown;
};

/** A scene camera to snapshot, resolved by node name via `player.scene.findNode({ name })`. */
export type CameraSnapshotSpec =
  | string
  | {
      /** Scene node name of the camera, e.g. `"Camera_Main"`. */
      cameraName: string;
      /** Merged over the shared `options.snapshot` for this camera only (`cameraId` is always overridden). */
      snapshot?: Omit<ThreekitSnapshotAsyncOptions, "cameraId">;
      /** Overrides the shared `options.filename` for this camera only. */
      filename?: string;
    };

/** Keyed by each spec's `cameraName` (or the plain string when a spec is a bare name). */
export type SnapshotCamerasResult = Record<string, SnapshotAndUploadResult>;

type PlayerWithSnapshot = {
  snapshotAsync?: (opts?: ThreekitSnapshotAsyncOptions) => Promise<string | Blob>;
};

type PlayerWithScene = {
  scene?: {
    findNode?: (query: {
      name: string;
      hierarchical?: boolean;
    }) => string | null | undefined;
  };
};

/**
 * Resolves a scene camera node's UUID by name via `player.scene.findNode({ name, hierarchical: true })`.
 * `hierarchical` is required — camera nodes normally live inside nested/proxied product assets, not
 * directly on the top-level scene node, and a plain `{ name }` query won't descend into those.
 * @see https://developer.threekit.com/reference/scene
 */
export function resolveCameraId(player: unknown, cameraName: string): string {
  const findNode = (player as PlayerWithScene).scene?.findNode;
  if (typeof findNode !== "function") {
    throw new Error(
      "resolveCameraId: player.scene.findNode is not available (Threekit player version)"
    );
  }
  const id = findNode({ name: cameraName, hierarchical: true });
  if (typeof id !== "string" || !id.trim()) {
    throw new Error(`resolveCameraId: no scene node found named "${cameraName}"`);
  }
  return id;
}

/** `https://{subdomain}.threekit.com/api/files` */
export function threekitFilesApiUrl(env: string): string {
  const trimmed = env.trim().replace(/^https?:\/\//, "").replace(/\/+$/, "");
  const host = trimmed.includes("threekit.com")
    ? trimmed
    : `${trimmed}.threekit.com`;
  return `https://${host}/api/files`;
}

/**
 * Public file bytes URL from upload `files[].id`.
 * @see https://developer.threekit.com/reference/uploadfiles
 */
export function threekitFileContentUrl(env: string, fileId: string): string {
  const id = fileId.trim();
  if (!id) {
    throw new Error("threekitFileContentUrl: file id is empty");
  }
  const base = threekitFilesApiUrl(env).replace(/\/+$/, "");
  return `${base}/${encodeURIComponent(id)}/content`;
}

/**
 * Decodes a `data:` URL into a `Blob` with no network activity.
 *
 * `player.snapshotAsync` can resolve with a base64 `data:image/...` URL. Reading that via
 * `fetch(dataUrl)` is subject to the page's CSP `connect-src` — and per the CSP spec, the
 * `*` source expression does NOT cover `data:`/`blob:`/`filesystem:` schemes, so even a
 * `connect-src *` policy (e.g. Oracle CPQ's) blocks it with
 * "Content-Security-Policy: ... blocked the loading of a resource (connect-src) at data:image/...".
 * Decoding manually avoids the fetch entirely, so no CSP directive applies.
 */
function dataUrlToBlob(dataUrl: string): Blob {
  const match = /^data:([^;,]*)?(;base64)?,(.*)$/s.exec(dataUrl);
  if (!match) {
    throw new Error("dataUrlToBlob: not a valid data: URL");
  }
  const [, mime = "application/octet-stream", isBase64, data] = match;
  let binary: string;
  if (isBase64) {
    binary = atob(data);
  } else {
    binary = decodeURIComponent(data);
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime || "application/octet-stream" });
}

function extensionForMime(mime: string): string {
  if (mime === "image/jpeg" || mime === "image/jpg") return "jpg";
  if (mime === "image/webp") return "webp";
  return "png";
}

function extractHttpUrlFromEntry(entry: Record<string, unknown>): string | null {
  const keys = [
    "url",
    "src",
    "href",
    "cdnUrl",
    "permalink",
    "location",
    "path",
    "publicUrl",
  ];
  for (const k of keys) {
    const v = entry[k];
    if (typeof v === "string" && (v.startsWith("http://") || v.startsWith("https://"))) {
      return v;
    }
  }
  const nested = entry.asset;
  if (isRecord(nested)) {
    for (const k of keys) {
      const v = nested[k];
      if (typeof v === "string" && (v.startsWith("http://") || v.startsWith("https://"))) {
        return v;
      }
    }
  }
  return null;
}

/**
 * Calls `player.snapshotAsync`, turns the result into a `Blob`, uploads via `POST /api/files`,
 * and returns a stable https URL from the response when present.
 */
function resolveBrowserUploadTarget(
  env: string,
  orgId: string | undefined,
  filesUploadProxyPath: string | null | undefined
): { url: string; sendBearer: boolean } {
  const path = (filesUploadProxyPath ?? "").trim();
  if (path) {
    let url: URL;
    if (/^https?:\/\//i.test(path)) {
      url = new URL(path);
    } else {
      const rel = path.startsWith("/") ? path : `/${path}`;
      if (typeof globalThis === "undefined" || !("location" in globalThis)) {
        throw new Error(
          "snapshotAndUpload: files upload proxy requires a browser (window.location)"
        );
      }
      const origin = (globalThis as unknown as Window).location?.origin;
      if (!origin) {
        throw new Error("snapshotAndUpload: could not resolve window.location.origin");
      }
      url = new URL(`${origin}${rel}`);
    }
    // The proxy picks the private API token + Threekit host from this pair
    // (server/lib/threekit-environments.mjs).
    url.searchParams.set("environment", env);
    if (orgId) url.searchParams.set("orgId", orgId);
    return { url: url.toString(), sendBearer: false };
  }
  return {
    url: threekitFilesApiUrl(env),
    sendBearer: true,
  };
}

function shouldLogSnapshotUpload(options?: SnapshotAndUploadOptions): boolean {
  if (options?.debug === true) return true;
  try {
    if (import.meta.env?.VITE_THREEKIT_FILES_UPLOAD_DEBUG === "true") return true;
    if (import.meta.env?.DEV === true) return true;
  } catch {
    /* embed / non-Vite bundles may omit import.meta.env */
  }
  if (typeof globalThis !== "undefined" && "location" in globalThis) {
    try {
      const w = globalThis as unknown as Window;
      const q = new URLSearchParams(w.location.search).get(
        "threekitFilesUploadDebug"
      );
      if (q === "1" || q === "true") return true;
      if (w.localStorage?.getItem("threekitFilesUploadDebug") === "1") return true;
    } catch {
      /* private mode / opaque origins */
    }
  }
  return false;
}

function logSnapshotUpload(
  phase: "request" | "response",
  payload: Record<string, unknown>
): void {
  // console.log: visible with default “All levels” / Standard in devtools (Info is easy to filter out)
  console.log("[threekit-files-upload]", phase, payload);
}

export async function snapshotAndUploadConfiguration(args: {
  env: string;
  /** Org id the player was mounted with; with `env`, selects the environment for the proxy. */
  orgId?: string;
  /** Public player token; not sent when uploading via same-origin `filesUploadProxyPath`. */
  authToken: string;
  player: unknown;
  options?: SnapshotAndUploadOptions;
  /** Same-origin path (e.g. `/api/threekit-files`); server adds the environment's private token. */
  filesUploadProxyPath?: string | null;
}): Promise<SnapshotAndUploadResult> {
  const { authToken, player, options = {}, filesUploadProxyPath } = args;
  const env = options.env?.trim() || args.env;
  const orgId = options.orgId?.trim() || args.orgId?.trim() || undefined;
  const snapper = player as PlayerWithSnapshot;
  if (typeof snapper.snapshotAsync !== "function") {
    throw new Error(
      "snapshotAndUpload: player.snapshotAsync is not available (Threekit player version / display mode)"
    );
  }

  const snapResult = await snapper.snapshotAsync(options.snapshot);
  let blob: Blob;
  if (typeof snapResult === "string" && snapResult.startsWith("data:")) {
    // Decode locally — no network request, so no connect-src CSP check applies.
    blob = dataUrlToBlob(snapResult);
  } else if (typeof snapResult === "string") {
    const res = await fetch(snapResult);
    if (!res.ok) {
      throw new Error(
        `snapshotAndUpload: could not read snapshot resource (${res.status})`
      );
    }
    blob = await res.blob();
  } else if (snapResult instanceof Blob) {
    blob = snapResult;
  } else {
    throw new Error("snapshotAndUpload: snapshotAsync returned an unexpected type");
  }

  const mime =
    blob.type ||
    (options.snapshot?.mimeType as string | undefined) ||
    "image/png";
  const ext = extensionForMime(mime);
  const filename =
    options.filename?.trim() || `configuration-snapshot.${ext}`;

  const fd = new FormData();
  fd.append("files", blob, filename);

  const target = resolveBrowserUploadTarget(env, orgId, filesUploadProxyPath);
  const directThreekitFilesUrl = threekitFilesApiUrl(env);
  const debug = shouldLogSnapshotUpload(options);
  if (debug) {
    logSnapshotUpload("request", {
      uploadUrl: target.url,
      mode: target.sendBearer ? "direct" : "same-origin-proxy",
      threekitEnv: env,
      orgId,
      directThreekitFilesUrl,
      multipartFilename: filename,
      sendsAuthorizationBearer: target.sendBearer,
      bearerTokenPreview: target.sendBearer
        ? threekitTokenPreview(authToken)
        : undefined,
      bearerTokenNote: target.sendBearer
        ? "Browser sends Authorization with player token (preview above)."
        : "Browser sends no Bearer; proxy adds THREEKIT_API_TOKEN. With THREEKIT_FILES_PROXY_DEBUG=1 (Docker) or =true (Vite), see docker logs / terminal at request start and response headers below (X-Threekit-Proxy-Token-Preview).",
    });
  }
  const headers: Record<string, string> = {};
  if (target.sendBearer) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  let uploadRes: Response;
  try {
    uploadRes = await fetch(target.url, {
      method: "POST",
      headers,
      body: fd,
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    const corsHint =
      target.sendBearer &&
      (target.url.includes("threekit.com") || target.url.includes("3kit.com"))
        ? " Browsers block reading cross-origin POST /api/files (CORS). Set VITE_THREEKIT_FILES_PROXY=/api/threekit-files at build time and mount({ filesUploadProxyPath: '/api/threekit-files' }) so uploads hit the same origin as this page; the host must proxy that path with THREEKIT_API_TOKEN (see .env.example + Dockerfile)."
        : target.sendBearer
          ? ""
          : " Check that this origin serves POST for your files-upload proxy path and nginx/Vite is up.";
    throw new Error(`snapshotAndUpload: upload fetch failed (${msg}).${corsHint}`);
  }

  const text = await uploadRes.text();
  if (debug) {
    logSnapshotUpload("response", {
      uploadUrl: target.url,
      httpStatus: uploadRes.status,
      responseBodyPreview: text.slice(0, 500),
      proxyTokenPreview:
        uploadRes.headers.get("x-threekit-proxy-token-preview") ?? undefined,
      proxyUpstreamHeader:
        uploadRes.headers.get("x-threekit-proxy-upstream") ?? undefined,
    });
  }
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(
      `snapshotAndUpload: files API returned non-JSON (HTTP ${uploadRes.status})`
    );
  }

  if (!uploadRes.ok) {
    let hint = "";
    if (uploadRes.status === 403) {
      const hdrPreview =
        uploadRes.headers.get("x-threekit-proxy-token-preview") ?? "";
      const proxyHint = target.sendBearer
        ? ""
        : hdrPreview
          ? ` Proxy used THREEKIT_API_TOKEN fingerprint ${hdrPreview} (see X-Threekit-Proxy-Token-Preview). If that matches your private token, Threekit is denying authorization for POST /api/files (user who created the token, org, or host vs ${uploadRes.headers.get("x-threekit-proxy-upstream") ?? directThreekitFilesUrl}).`
          : " With same-origin proxy, nginx/Vite sends THREEKIT_API_TOKEN — not the browser player token; enable THREEKIT_FILES_PROXY_DEBUG on the proxy to echo a token fingerprint in response headers and logs. Otherwise 403 usually means wrong token type/org, or proxy host does not match your org.";
      hint =
        " Hint: POST /api/files requires a private org API token (Threekit Admin → Tokens), not the public embed/player token. Set THREEKIT_API_TOKEN on the proxy (Docker: -e THREEKIT_API_TOKEN=…; host .env is not loaded in the container)." +
        proxyHint;
    } else if (uploadRes.status === 401) {
      hint =
        " Hint: Invalid or missing Bearer for Files API — set THREEKIT_API_TOKEN for the same-origin proxy, or use a token Threekit accepts for POST /api/files.";
    }
    throw new Error(
      `snapshotAndUpload: files API HTTP ${uploadRes.status}: ${text.slice(0, 800)}${hint}`
    );
  }

  const files =
    isRecord(json) && Array.isArray(json.files)
      ? json.files
      : Array.isArray(json)
        ? json
        : null;
  if (!files || files.length === 0) {
    throw new Error(
      "snapshotAndUpload: files API returned no `files` array — inspect `raw` in devtools"
    );
  }

  const first = files[0];
  if (!isRecord(first)) {
    throw new Error("snapshotAndUpload: unexpected entry in `files` array");
  }

  let url = extractHttpUrlFromEntry(first);
  if (!url) {
    const id = first.id;
    if (typeof id === "string" && id.trim()) {
      url = threekitFileContentUrl(env, id);
    }
  }
  if (!url) {
    throw new Error(
      "snapshotAndUpload: file entry has no https url field and no usable `id` for …/api/files/{id}/content"
    );
  }

  return { url, file: first, raw: json };
}

/**
 * Snapshots and uploads once per named camera (resolved via {@link resolveCameraId}), sequentially
 * (the player renders/captures one view at a time). Each camera's `cameraId` always overrides
 * `options.snapshot.cameraId` / the spec's own `snapshot.cameraId`.
 */
export async function snapshotCamerasAndUpload(args: {
  env: string;
  orgId?: string;
  authToken: string;
  player: unknown;
  cameras: CameraSnapshotSpec[];
  /** Shared defaults; each camera's own `snapshot`/`filename` (if set) take precedence. */
  options?: SnapshotAndUploadOptions;
  filesUploadProxyPath?: string | null;
}): Promise<SnapshotCamerasResult> {
  const { env, orgId, authToken, player, cameras, options, filesUploadProxyPath } = args;
  const result: SnapshotCamerasResult = {};
  for (const cam of cameras) {
    const spec = typeof cam === "string" ? { cameraName: cam } : cam;
    const cameraId = resolveCameraId(player, spec.cameraName);
    result[spec.cameraName] = await snapshotAndUploadConfiguration({
      env,
      orgId,
      authToken,
      player,
      filesUploadProxyPath,
      options: {
        ...options,
        filename: spec.filename ?? options?.filename,
        snapshot: { ...options?.snapshot, ...spec.snapshot, cameraId },
      },
    });
  }
  return result;
}

/**
 * The 3 cameras this product's scene ships with, keyed by a short alias (scene node names are
 * awkward as JS keys / CPQ Model.js identifiers — e.g. `"Camera (Perspective)"`).
 * @see https://developer.threekit.com/reference/scene
 */
export const STANDARD_SNAPSHOT_CAMERAS = {
  main: "Camera (Perspective)",
  front: "Snapshot_Front",
  side: "Snapshot_Side",
} as const;

export type StandardSnapshotCameraKey = keyof typeof STANDARD_SNAPSHOT_CAMERAS;

export type SnapshotStandardCamerasResult = Record<
  StandardSnapshotCameraKey,
  SnapshotAndUploadResult
>;

/**
 * {@link snapshotCamerasAndUpload} with the 3 {@link STANDARD_SNAPSHOT_CAMERAS} hardcoded, so
 * callers (e.g. CPQ) don't need to know or pass scene camera names — just the shared `options`.
 */
export async function snapshotStandardCamerasAndUpload(args: {
  env: string;
  orgId?: string;
  authToken: string;
  player: unknown;
  options?: SnapshotAndUploadOptions;
  filesUploadProxyPath?: string | null;
}): Promise<SnapshotStandardCamerasResult> {
  const byCameraName = await snapshotCamerasAndUpload({
    ...args,
    cameras: Object.values(STANDARD_SNAPSHOT_CAMERAS),
  });
  const result = {} as SnapshotStandardCamerasResult;
  for (const key of Object.keys(STANDARD_SNAPSHOT_CAMERAS) as StandardSnapshotCameraKey[]) {
    result[key] = byCameraName[STANDARD_SNAPSHOT_CAMERAS[key]];
  }
  return result;
}
